/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useAddProduct } from '../hooks/useAddProduct';
import { useProductById } from '../hooks/useProductById';
import { useUpdateProduct } from '../hooks/useUpdateProduct';
import MultiSelectDropdown from 'components/MultiSelectDropdown';
import SelectedChips from 'components/SelectedChips';
import { useProtocolPage } from 'views/admin/protocol/hooks/useProtocolPage';

function ProductManagementForm({ editId, onSuccess, setSelectedId }) {
  const id = editId?._id;
  const [selectedSystems, setSelectedSystems] = useState([]);
  const [selectedDiseases, setSelectedDiseases] = useState([]);
  const [diseases, setDiseases] = useState([]);
  const [selectedDiseaseObjects, setSelectedDiseaseObjects] = useState([]);
  const [activeDisease, setActiveDisease] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      systems: '',
      diseases: '',
      name: '',
      details: '',
    },
  });

  const { data, isLoading: isIdLoading, isError } = useProductById(id);

  useEffect(() => {
    if (data?.data?.result) {
      reset({
        name: data.data.result.name,
        details: data.data.result.details,
      });

      // Populate Systems
      if (data.data.result.systems && Array.isArray(data.data.result.systems)) {
        setSelectedSystems(data.data.result.systems);
        setValue('systems', data.data.result.systems);
      }

      // Populate Diseases
      if (
        data.data.result.diseases &&
        Array.isArray(data.data.result.diseases)
      ) {
        setSelectedDiseases(data.data.result.diseases);
        setValue('diseases', data.data.result.diseases);
      }
    }
  }, [data, reset]);

  const createMutation = useAddProduct({
    onSuccess: () => {
      reset();
      onSuccess?.();
    },
  });
  const updateMutation = useUpdateProduct({
    onSuccess: () => {
      reset();

      setSelectedId(null);
      onSuccess?.();
    },
  });

  const submitHandler = (formData) => {
    if (id) {
      updateMutation.mutate({ id: id, ...formData });
      return;
    }
    createMutation.mutate(formData);
  };

  const isLoading =
    createMutation.isPending || updateMutation.isPending || isIdLoading;

  const cardBg = useColorModeValue('white', 'gray.800');
  const cardShadow = useColorModeValue(
    'rgba(0, 0, 0, 0.35) 0px 5px 15px',
    'rgba(0, 0, 0, 0.6) 0px 5px 15px',
  );
  const blueText = useColorModeValue('blue.600', 'blue.200');
  const chipBg = useColorModeValue('blue.100', 'blue.700');
  const chipText = useColorModeValue('gray.800', 'white');
  const warningTextColor = useColorModeValue('gray.800', 'gray.200'); // Defined top-level

  const { systems, diseases: fetchedDiseases } =
    useProtocolPage(selectedSystems);

  const systemOptions = systems?.map((item) => ({
    label: `${item.system} - ${item.systemHi}`,
    value: item.system,
  }));
  const systemLabelMap = React.useMemo(() => {
    const map = {};
    systemOptions?.forEach((opt) => {
      map[opt.value] = opt.label;
    });
    return map;
  }, [systemOptions]);
  /* ===================== EFFECTS ===================== */
  useEffect(() => {
    if (fetchedDiseases) {
      setDiseases(fetchedDiseases);

      // Sync disease objects for Edit mode if needed
      if (selectedDiseases.length > 0 && selectedDiseaseObjects.length === 0) {
        const objects = fetchedDiseases.filter((d) =>
          selectedDiseases.includes(d._id),
        );
        setSelectedDiseaseObjects(objects);
      }
    }
  }, [fetchedDiseases, selectedDiseases, selectedDiseaseObjects.length]);

  useEffect(() => {
    if (selectedSystems.length === 0) {
      setSelectedDiseases([]);
      setSelectedDiseaseObjects([]);
      setDiseases([]);
      setValue('systems', []); // Clear form value
      setValue('diseases', []); // Clear form value
    } else {
      setValue('systems', selectedSystems); // Sync form value
      trigger('systems');
    }
  }, [selectedSystems, setValue, trigger]);

  // Effect to sync diseases with form
  useEffect(() => {
    setValue('diseases', selectedDiseases);
    if (selectedDiseases.length > 0) trigger('diseases');
  }, [selectedDiseases, setValue, trigger]);

  // Register custom fields
  useEffect(() => {
    register('systems', { required: 'System selection is required' });
    register('diseases', { required: 'Disease selection is required' });
  }, [register]);

  /* ===================== SYSTEM HANDLERS ===================== */
  const handleSelectSystem = (systemCode) => {
    setSelectedSystems((prev) => {
      if (prev.includes(systemCode)) {
        return prev.filter((s) => s !== systemCode);
      }
      return [...prev, systemCode];
    });
  };

  const handleRemoveSystem = (systemCode) => {
    setSelectedSystems((prev) => prev.filter((s) => s !== systemCode));
    setSelectedDiseaseObjects((prev) =>
      prev.filter((d) => d.system !== systemCode),
    );
    setSelectedDiseases((prev) =>
      prev.filter((id) => {
        const disease = diseases.find((d) => d._id === id);
        return disease?.system !== systemCode;
      }),
    );
    // reset active dropdown
    setActiveDisease('');
  };

  /* ===================== DISEASE HANDLERS ===================== */
  const handleSelectDisease = (id) => {
    setSelectedDiseases((prev) => {
      if (prev.includes(id)) {
        return prev.filter((dId) => dId !== id);
      }
      return [...prev, id];
    });

    setSelectedDiseaseObjects((prev) => {
      const disease = diseases.find((d) => d._id === id);
      if (!disease) return prev;

      const exists = prev.some((d) => d._id === id);

      if (exists) {
        return prev.filter((d) => d._id !== id);
      }

      return [...prev, disease];
    });
  };

  const handleRemoveDisease = (disease) => {
    setSelectedDiseases((p) => p.filter((id) => id !== disease._id));
    setSelectedDiseaseObjects((p) => p.filter((d) => d._id !== disease._id));
  };

  return (
    <Box w="100%" mt={6} display="flex" justifyContent="center">
      <Card
        w="100%"
        maxW="800px"
        rounded="xl"
        bg={cardBg}
        boxShadow={cardShadow}
      >
        <CardBody p={6}>
          <form onSubmit={handleSubmit(submitHandler)}>
            <VStack spacing={6} align="stretch">
              <Flex
                direction={{ base: 'column', md: 'row' }}
                gap={6}
                align="flex-start"
                justifyContent={'space-between'}
              >
                {/* System Field */}
                <Box w={{ base: '100%', md: '48%' }}>
                  <FormControl mb={1} isInvalid={!!errors.systems}>
                    <FormLabel
                      color={blueText}
                      fontWeight="bold"
                      fontSize="xs"
                      textTransform="uppercase"
                    >
                      Select System/Axis
                    </FormLabel>

                    <MultiSelectDropdown
                      label="System"
                      placeholder="System / Axis"
                      options={systemOptions}
                      selectedValues={selectedSystems}
                      onToggle={handleSelectSystem}
                    />

                    <FormErrorMessage>
                      {errors.systems && errors.systems.message}
                    </FormErrorMessage>
                  </FormControl>

                  <Box mt={2}>
                    <SelectedChips
                      items={selectedSystems}
                      getKey={(sys) => sys}
                      getLabel={(sys) => systemLabelMap[sys] || sys}
                      onRemove={handleRemoveSystem}
                      chipBg={chipBg}
                      chipText={chipText}
                    />
                  </Box>
                </Box>
                <Box w={{ base: '100%', md: '48%' }}>
                  <FormControl mb={1} isInvalid={!!errors.diseases}>
                    <FormLabel
                      color={blueText}
                      fontWeight="bold"
                      fontSize="xs"
                      textTransform="uppercase"
                    >
                      Select Disease/Symptoms
                    </FormLabel>

                    <MultiSelectDropdown
                      label="Disease"
                      placeholder="Choose Disease / Symptoms"
                      options={diseases.map((d) => ({
                        label: d.diseaseHindi
                          ? `${d.diseaseEnglish} - ${d.diseaseHindi}`
                          : d.diseaseEnglish,
                        value: d._id,
                      }))}
                      selectedValues={selectedDiseases}
                      onToggle={handleSelectDisease}
                      isDisabled={selectedSystems.length === 0}
                    />

                    <FormErrorMessage>
                      {errors.diseases && errors.diseases.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Modern Tooltip/Warning Style */}

                  <SelectedChips
                    items={selectedDiseaseObjects}
                    getKey={(d) => d._id}
                    getLabel={(d) =>
                      d.diseaseHindi
                        ? `${d.diseaseEnglish} - ${d.diseaseHindi}`
                        : d.diseaseEnglish
                    }
                    onRemove={handleRemoveDisease}
                    chipBg={chipBg}
                    chipText={chipText}
                  />
                </Box>
              </Flex>

              <FormControl isInvalid={!!errors.name} w="100%">
                <FormLabel
                  color={blueText}
                  fontWeight="bold"
                  fontSize="xs"
                  textTransform="uppercase"
                >
                  Name{' '}
                  <Text as="span" color="red.500">
                    *
                  </Text>
                </FormLabel>
                <Input
                  placeholder="Enter Name"
                  {...register('name', { required: 'Name is required' })}
                />
                <FormErrorMessage>
                  {errors.name && errors.name.message}
                </FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.details}>
                <FormLabel fontWeight="bold">Details </FormLabel>
                <Textarea
                  placeholder="Enter Details"
                  rows={6}
                  resize="vertical"
                  {...register('details', {
                    // required: 'Details are required',
                  })}
                />
                {errors.details && (
                  <Text color="red.500" fontSize="13px" mt={1}>
                    {errors.details.message}
                  </Text>
                )}
              </FormControl>

              <Flex justify="flex-end" mt={4}>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={isLoading}
                  px={8}
                >
                  {editId ? 'Update' : 'Submit'}
                </Button>
              </Flex>
            </VStack>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
}

export default ProductManagementForm;
